import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Header from '../components/Header';

export default function About() {
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Helmet>
        <title>About | WA BHA Treatment Finder</title>
        <meta name="description" content="Learn about the WA BHA Treatment Finder — a directory of Washington State behavioral health treatment facilities built for healthcare professionals using WA DOH Greenbook data." />
      </Helmet>
      <Header
        right={
          <Link
            to="/login"
            className="text-blue-200 hover:text-white text-sm underline underline-offset-2 transition-colors"
          >
            Sign in →
          </Link>
        }
      />

      <main className="flex-1 max-w-3xl mx-auto px-6 py-12 w-full">

        {/* Hero */}
        <div className="mb-10">
          <h1 className="text-3xl font-bold text-gray-900 mb-3">
            Washington State Behavioral Health Treatment Finder
          </h1>
          <p className="text-lg text-gray-600 leading-relaxed">
            A professional-grade directory of Washington State behavioral health treatment
            facilities — built for case managers, social workers, discharge planners, and
            other healthcare professionals who need fast, accurate placement information.
          </p>
        </div>

        {/* What it is */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-3">What is this?</h2>
          <p className="text-gray-600 leading-relaxed">
            The WA BHA Treatment Finder is a searchable, filterable directory of all licensed
            behavioral health facilities in Washington State. Data is sourced directly from the
            Washington State Department of Health Greenbook — the official registry of licensed
            behavioral health agencies — and updated monthly.
          </p>
        </section>

        {/* Who it's for */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-3">Who is it for?</h2>
          <p className="text-gray-600 leading-relaxed mb-3">
            This tool is designed for licensed healthcare professionals who need to locate
            appropriate treatment placements quickly and accurately:
          </p>
          <ul className="list-disc list-inside text-gray-600 space-y-1.5 ml-2">
            <li>Case managers and care coordinators</li>
            <li>Social workers and discharge planners</li>
            <li>Emergency department staff</li>
            <li>Mental health providers</li>
            <li>Crisis intervention teams</li>
            <li>Behavioral health organizations</li>
          </ul>
        </section>

        {/* What you can search */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-3">What can you search?</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { label: 'By County', desc: 'Filter by any of Washington\'s 39 counties' },
              { label: 'By Population', desc: 'Adult, youth, or both' },
              { label: 'By Service Type', desc: 'Mental health, SUD, or co-occurring' },
              { label: 'By Setting', desc: 'Inpatient, outpatient, or detox' },
            ].map(({ label, desc }) => (
              <div key={label} className="bg-white border border-gray-200 rounded-xl px-5 py-4">
                <p className="font-semibold text-gray-800 text-sm mb-0.5">{label}</p>
                <p className="text-gray-500 text-sm">{desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Data source */}
        <section className="mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-3">Data source</h2>
          <p className="text-gray-600 leading-relaxed">
            Facility data is sourced from the{' '}
            <strong>Washington State Department of Health Greenbook</strong> — the official
            monthly-published registry of all licensed behavioral health agencies in the state.
            Each facility record includes licensed bed counts by type, service flags, address,
            and contact information. Data is updated monthly by the site administrator.
          </p>
        </section>

        {/* CTA */}
        <div className="bg-blue-700 rounded-2xl px-8 py-8 text-center">
          <h2 className="text-xl font-bold text-white mb-2">Ready to get access?</h2>
          <p className="text-blue-200 text-sm mb-5">
            Individual subscriptions and organization accounts available.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/signup"
              className="bg-white text-blue-700 font-semibold px-6 py-2.5 rounded-lg text-sm hover:bg-blue-50 transition-colors"
            >
              Create free account
            </Link>
            <Link
              to="/signup"
              className="bg-blue-600 text-white font-semibold px-6 py-2.5 rounded-lg text-sm border border-blue-500 hover:bg-blue-500 transition-colors"
            >
              Sign up
            </Link>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-gray-400 mt-8">
          Questions?{' '}
          <a
            href="mailto:watreatmentfinder@gmail.com"
            className="text-blue-600 hover:underline"
          >
            watreatmentfinder@gmail.com
          </a>
          {' '}·{' '}
          <Link to="/terms" className="text-blue-600 hover:underline">
            Terms of Service
          </Link>
        </p>
      </main>
    </div>
  );
}
